import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  schemaJson?: object;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogType = 'website',
  schemaJson,
}: SEOHeadProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonicalUrl);
    }

    setMeta('og:title', ogTitle || title, true);
    setMeta('og:description', ogDescription || description, true);
    setMeta('og:type', ogType, true);
    if (canonicalUrl) setMeta('og:url', canonicalUrl, true);

    setMeta('twitter:title', ogTitle || title);
    setMeta('twitter:description', ogDescription || description);

    let schemaScript: HTMLScriptElement | null = null;
    if (schemaJson) {
      const existing = document.querySelectorAll('script[type="application/ld+json"]');
      existing.forEach((s) => {
        if (s.getAttribute('data-dynamic') === 'true') s.remove();
      });
      schemaScript = document.createElement('script');
      schemaScript.type = 'application/ld+json';
      schemaScript.setAttribute('data-dynamic', 'true');
      schemaScript.textContent = JSON.stringify(schemaJson);
      document.head.appendChild(schemaScript);
    }

    return () => {
      if (schemaScript) schemaScript.remove();
    };
  }, [title, description, keywords, canonicalUrl, ogTitle, ogDescription, ogType, schemaJson]);

  return null;
}